import { useEffect, useState } from "react";
import { Copy, Download, Instagram, PenTool, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AuthImg } from "@/components/studio/AuthImg";
import { api, downloadBlob, errMsg } from "@/lib/api";

const Eyebrow = ({ children, color }) => (
  <div className="font-mono-meta text-[11px] tracking-[0.24em] uppercase" style={{ color }}>{children}</div>
);

export const InstagramCard = ({ p, theme, version, onPatch, onStarted }) => {
  const ig = p.instagram || {};
  const [caption, setCaption] = useState(ig.caption || "");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const drawing = p.illustration_job?.status === "running";
  const slug = p.files?.slug || "post";
  const v = `${version}-${p.illustration_job?.at || ""}`;

  useEffect(() => setCaption(ig.caption || ""), [ig.caption]);
  useEffect(() => {
    api.get(`/products/${p.id}/instagram/count`).then((r) => setCount(r.data.slides));
  }, [p.id, version]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/products/${p.id}/instagram/caption`);
      onPatch({ instagram: r.data });
      toast.success("Fresh caption written");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const saveCaption = () => {
    if (caption !== ig.caption) api.put(`/products/${p.id}/instagram/caption`, { caption });
  };
  const copy = () => {
    navigator.clipboard.writeText(`${caption}\n\n${(ig.hashtags || []).map((t) => `#${t}`).join(" ")}`);
    toast.success("Caption and hashtags copied");
  };
  const draw = async () => {
    try {
      await api.post(`/products/${p.id}/illustration`);
      toast.success("Drawing the illustration — about a minute");
      onStarted();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-12 border border-[#2A2325]/10 bg-[#FFFDF9]" data-testid="instagram-card">
      <div className="lg:col-span-3 p-6 flex flex-col items-center justify-center gap-4" style={{ background: theme.dark }}>
        <AuthImg path={`/products/${p.id}/instagram/post.png`} version={v} alt="Instagram post" className="w-full max-w-[240px] aspect-[4/5] object-cover shadow-lg" testId="instagram-post-img" />
        <button onClick={draw} disabled={drawing} className="text-xs flex items-center gap-1.5 hover:underline disabled:opacity-60" style={{ color: theme.accent }} data-testid="draw-illustration-btn">
          <PenTool size={13} /> {drawing ? "Drawing illustration…" : p.illustration_path ? "Redraw illustration" : "Draw a gold line illustration"}
        </button>
        {p.illustration_job?.status === "failed" && <span className="text-[11px] text-red-200 text-center" data-testid="illustration-error">{p.illustration_job.error}</span>}
      </div>
      <div className="lg:col-span-9 p-6 md:p-7 space-y-5 min-w-0">
        <div className="flex items-center justify-between">
          <Eyebrow color={theme.accent}><span className="inline-flex items-center gap-2"><Instagram size={14} /> Instagram post</span></Eyebrow>
          <span className="font-mono-meta text-[10px] tracking-[0.2em] text-[#5C5255] uppercase">{ig.kind || `new ${p.product_type || "guide"}`} · {(ig.hashtags || []).length} hashtags</span>
        </div>
        <Textarea rows={7} value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={saveCaption} placeholder="Click “Write caption” to let the AI write a launch caption with hashtags." className="bg-white text-sm leading-relaxed" data-testid="instagram-caption-input" />
        {ig.hashtags?.length > 0 && <p className="text-xs text-[#5C5255] line-clamp-2" data-testid="instagram-hashtags">{ig.hashtags.map((t) => `#${t}`).join(" ")}</p>}
        <div className="flex flex-wrap gap-2">
          <Button onClick={copy} disabled={!caption} className="rounded-none gap-2" style={{ background: theme.dark }} data-testid="copy-caption-btn"><Copy size={14} /> Copy caption + hashtags</Button>
          <Button variant="outline" className="rounded-none gap-2 bg-transparent" onClick={() => downloadBlob(`/products/${p.id}/instagram/post.png?download=1`, `${slug}-instagram.png`)} data-testid="download-post-image-btn"><Download size={14} /> Download image (1080×1350)</Button>
          <Button variant="ghost" className="rounded-none gap-2" onClick={generate} disabled={busy} data-testid="new-caption-btn"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {ig.caption ? "New version" : "Write caption"}</Button>
        </div>
        <div className="border-t border-[#2A2325]/10 pt-5">
          <div className="flex items-center justify-between mb-3">
            <Eyebrow color={theme.accent}>Carousel · {count} slides</Eyebrow>
            <Button variant="outline" size="sm" className="rounded-none gap-2 bg-transparent" onClick={() => downloadBlob(`/products/${p.id}/instagram/carousel.zip`, `${slug}-carousel.zip`)} data-testid="download-carousel-btn"><Download size={14} /> Download carousel (ZIP)</Button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="carousel-slides">
            {Array.from({ length: count }).map((_, i) => (
              <AuthImg key={i} path={`/products/${p.id}/instagram/slide/${i}.png`} version={v} alt={`Slide ${i + 1}`} className="w-full aspect-[4/5] object-cover" testId={`carousel-slide-${i}`} />
            ))}
          </div>
          <p className="text-xs text-[#5C5255] mt-2">Post the slides in order: cover, three chapters, “Inside the guide”, then “Read Chapter I free”. Prices are in the caption only. The ZIP includes caption.txt.</p>
        </div>
      </div>
    </div>
  );
};
